import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useEffect, useState, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Prompt, useHistory } from 'react-router-dom';
import { A } from '#modules/browser';
import { useDialog } from '#modules/dialog';
import { Env } from '#modules/env';
import { Tooltip } from '#modules/tooltip';
import { PageContext } from './contexts';
import { getCurrentMenuItemIdFromLocation } from './helpers';
import LNB from './LNB';

PageContent.propTypes = {
  empty: PropTypes.bool,
  fixed: PropTypes.bool,
  headless: PropTypes.bool,
  confirmOnLeave: PropTypes.bool,
  title: PropTypes.string,
  parentURL: PropTypes.string,
  tooltip: PropTypes.node,
  status: PropTypes.string,
  menuItemId: PropTypes.string,
  children: PropTypes.node.isRequired,
};
export default function PageContent({
  empty = false,
  fixed = false,
  headless = false,
  confirmOnLeave = false,
  title,
  parentURL,
  tooltip,
  status,
  menuItemId = getCurrentMenuItemIdFromLocation(),
  children,
}) {
  const { t } = useTranslation();
  const { gnbBackgroundColor, gnbFontColor } = useContext(PageContext);

  useEffect(() => {
    if (!menuItemId) {
      console.warn('menuItemId:', menuItemId, '-> `menuItemId` is required.');
      return;
    }

    const lowerId = menuItemId.toLowerCase();
    const menuItemName = t(`menu_item_${lowerId}`, { context: Env.TARGET });
    document.title = `${title ?? t('title')} : ${menuItemName}`;
    window.name = `works_settings_${lowerId}`;
    return () => {
      document.title = '';
      window.name = '';
    };
  }, []);

  const history = useHistory();
  const dialog = useDialog();
  const [shouldLeave, setShouldLeave] = useState(false);
  const nextLocationStringRef = useRef();
  const [promptDialogOpened, setPromptDialogOpened] = useState(false);
  const handlePrompt = (nextLocation) => {
    if (shouldLeave) return true;
    if (promptDialogOpened) return false;

    const { pathname, search, hash } = nextLocation;
    setPromptDialogOpened(true);
    dialog.alert((action) => ({
      title: t('content_leave_confirm_message'),
      onClose: () => setPromptDialogOpened(false),
      cancel: true,
      [action(t('button_exit'))]: (close) => {
        nextLocationStringRef.current = `${pathname}${search}${hash}`;
        setShouldLeave(true);
        close();
      },
    }));
    return false;
  };
  useEffect(() => {
    if (!shouldLeave) return;
    history.push(nextLocationStringRef.current);
  }, [shouldLeave]);

  useEffect(() => {
    if (!confirmOnLeave) return;

    const onBeforeunload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeunload);
    return () => window.removeEventListener('beforeunload', onBeforeunload);
  }, [confirmOnLeave]);

  return (
    <>
      <header
        className={classNames('header', {
          hide: headless,
          light_theme: ['#000', '#000000'].includes(gnbFontColor),
        })}
        style={{ backgroundColor: gnbBackgroundColor }}
      >
        <h1 style={{ color: gnbFontColor }}>{t('title')}</h1>
      </header>
      {empty ? null : <LNB menuItemId={menuItemId} />}
      <div className="container" role="main">
        {title && parentURL && (
          <div className="contents_header">
            <h3 className="title">
              <A className="text" href={parentURL}>
                {title}
              </A>
              {tooltip && <Tooltip>{tooltip}</Tooltip>}
              {status && <span className="status">{status}</span>}
            </h3>
          </div>
        )}
        <div
          className={classNames('contents_body', {
            fixed_layout: fixed || empty,
          })}
        >
          {empty ? (
            children
          ) : (
            <div className="contents_body_inner">{children}</div>
          )}
        </div>
      </div>
      <Prompt when={confirmOnLeave} message={handlePrompt} />
    </>
  );
}
